import { LocalDate } from "@js-joda/core";
import { createPortal } from "react-dom";
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { ZIndex } from "config/zIndex";
import {
  UNMEASURED_ANCHORED_POSITION,
  type AnchoredPosition,
} from "components/designV2/anchoredPosition.logic";
import {
  getCalendarLabels,
  getStockholmLocalDateNow,
} from "components/designV2/datePicker/datePicker.utils";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { useParentZIndex } from "components/designV2/stacking/stackingZIndexContext";
import { useAnchoredPosition } from "components/designV2/useAnchoredPosition";
import {
  Container,
  MonthButton,
  MonthGrid,
  MonthLabel,
  NavButton,
  PeriodButton,
  Popup,
  PopupHeader,
  PopupYear,
  TodayButton,
  YearNavButton,
} from "./monthPeriodNavigator.styled";

const MIN_YEAR = 2000;

export interface MonthPeriodNavigatorProps {
  readonly currentDate: LocalDate;
  readonly showTodayButton?: boolean;
  readonly onChange: (nextDate: LocalDate) => void;
}

interface PeriodPickerPopupProps {
  readonly currentMonth: number;
  readonly currentYear: number;
  readonly draftYear: number;
  readonly isMeasured: boolean;
  readonly monthNames: readonly string[];
  readonly position: AnchoredPosition;
  readonly popupRef: RefObject<HTMLDivElement>;
  readonly popupZIndex: number;
  readonly onMonthSelect: (month: number) => void;
  readonly onYearChange: (year: number) => void;
}

const PeriodPickerPopup = (props: PeriodPickerPopupProps): JSX.Element => {
  const { t } = useTranslation();

  return createPortal(
    <Popup
      ref={props.popupRef}
      role="dialog"
      aria-label={t("DatePicker.SelectPeriod")}
      $left={props.position.left}
      $top={props.position.top}
      $zIndex={props.popupZIndex}
      $isMeasured={props.isMeasured}
    >
      <PopupHeader>
        <YearNavButton
          type="button"
          aria-label={t("DatePicker.PreviousYear")}
          disabled={props.draftYear <= MIN_YEAR}
          onClick={() => props.onYearChange(props.draftYear - 1)}
        >
          <CustomIcon
            name="v2/left-chevron"
            size="16px"
            color={props.draftYear <= MIN_YEAR ? ColorV2.Blue5 : ColorV2.Blue2}
          />
        </YearNavButton>
        <PopupYear>{props.draftYear}</PopupYear>
        <YearNavButton
          type="button"
          aria-label={t("DatePicker.NextYear")}
          onClick={() => props.onYearChange(props.draftYear + 1)}
        >
          <CustomIcon
            name="v2/right-chevron"
            size="16px"
            color={ColorV2.Blue2}
          />
        </YearNavButton>
      </PopupHeader>
      <MonthGrid>
        {props.monthNames.map((name, index) => (
          <MonthButton
            key={name}
            type="button"
            $isSelected={
              props.draftYear === props.currentYear &&
              index + 1 === props.currentMonth
            }
            aria-label={name}
            aria-pressed={
              props.draftYear === props.currentYear &&
              index + 1 === props.currentMonth
            }
            onClick={() => props.onMonthSelect(index + 1)}
          >
            {name.slice(0, 3)}
          </MonthButton>
        ))}
      </MonthGrid>
    </Popup>,
    document.body,
  );
};

export const MonthPeriodNavigator = (
  props: MonthPeriodNavigatorProps,
): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [draftYear, setDraftYear] = useState(props.currentDate.year());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const parentZIndex = useParentZIndex();
  const popupZIndex = parentZIndex != null ? parentZIndex + 1 : ZIndex.dropdown;
  const position = useAnchoredPosition({
    floatingRef: popupRef,
    isOpen,
    minWidth: 264,
    triggerRef,
    widthMode: "content",
  });

  useEffect(() => {
    setDraftYear(props.currentDate.year());
  }, [props.currentDate]);

  const closePicker = useCallback((): void => {
    setIsOpen(false);
  }, []);

  useOnClickOutside([triggerRef, popupRef], closePicker, {
    enabled: isOpen,
    eventType: "mousedown",
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") closePicker();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closePicker, isOpen]);

  const { monthNames } = useMemo(() => getCalendarLabels(t), [t]);
  const previousDate = props.currentDate.minusMonths(1);
  const nextDate = props.currentDate.plusMonths(1);
  const monthName = monthNames[props.currentDate.monthValue() - 1] ?? "";
  const today = getStockholmLocalDateNow();

  const selectMonth = (month: number): void => {
    props.onChange(LocalDate.of(draftYear, month, 1));
    closePicker();
  };

  const togglePicker = (): void => {
    if (!isOpen) setDraftYear(props.currentDate.year());
    setIsOpen((open) => !open);
  };

  return (
    <>
      <Container>
        <NavButton
          type="button"
          aria-label={t("DatePicker.PreviousMonth")}
          onClick={() => props.onChange(previousDate)}
        >
          <CustomIcon
            name="v2/left-chevron"
            size="20px"
            color={ColorV2.Blue2}
          />
        </NavButton>
        <PeriodButton
          ref={triggerRef}
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={t("DatePicker.SelectPeriod")}
          onClick={togglePicker}
        >
          <MonthLabel>
            {monthName} {props.currentDate.year()}
          </MonthLabel>
          <MonthLabel $isSubLabel>
            1 - {props.currentDate.lengthOfMonth()} {monthName}
          </MonthLabel>
        </PeriodButton>
        {props.showTodayButton && (
          <TodayButton
            type="button"
            aria-label={t("DatePicker.Today")}
            disabled={props.currentDate.equals(today)}
            onClick={() => {
              props.onChange(today);
              closePicker();
            }}
          >
            {t("DatePicker.Today")}
          </TodayButton>
        )}
        <NavButton
          type="button"
          aria-label={t("DatePicker.NextMonth")}
          onClick={() => props.onChange(nextDate)}
        >
          <CustomIcon
            name="v2/right-chevron"
            size="20px"
            color={ColorV2.Blue2}
          />
        </NavButton>
      </Container>
      {isOpen && typeof document !== "undefined" ? (
        <PeriodPickerPopup
          popupRef={popupRef}
          currentMonth={props.currentDate.monthValue()}
          currentYear={props.currentDate.year()}
          draftYear={draftYear}
          isMeasured={position != null}
          monthNames={monthNames}
          position={position ?? UNMEASURED_ANCHORED_POSITION}
          popupZIndex={popupZIndex}
          onMonthSelect={selectMonth}
          onYearChange={setDraftYear}
        />
      ) : null}
    </>
  );
};
